/**
 * WO#4B — Territory Identity API client.
 *
 * Authenticated owner-managed surface. All requests carry the SIWE session
 * cookie (`credentials: "include"`). The SAVE is a state-changing mutation:
 * the browser attaches the Origin header automatically (trusted-Origin guard is
 * enforced server-side).
 */
import { API_BASE } from "./api";
import type {
  ReconcileResponse,
  ResolveResponse,
  SaveResponse,
} from "../domain/territory-identity";

/** Typed error carrying the backend envelope `code` + HTTP status. */
export class TerritoryIdentityError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "TerritoryIdentityError";
    this.status = status;
    this.code = code;
  }
}

// ── Session-bound CSRF token (WO#4B). Memory-only; never localStorage. Bound to
// the current SIWE session server-side; cleared on wallet switch / disconnect /
// logout so a stale wallet-A token can never authorize a wallet-B mutation.
let _csrfToken: string | null = null;

export function clearCsrfToken(): void {
  _csrfToken = null;
}

export async function getCsrfToken(force = false): Promise<string> {
  if (_csrfToken && !force) return _csrfToken;
  const resp = await fetch(`${API_BASE}/api/v1/auth/csrf`, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });
  if (!resp.ok) {
    _csrfToken = null;
    const code = resp.status === 401 ? "UNAUTHENTICATED" : "CSRF_FETCH_FAILED";
    throw new TerritoryIdentityError(resp.status, code, "Could not obtain a security token.");
  }
  _csrfToken = (await resp.json()).csrf_token as string;
  return _csrfToken;
}

/** Shape of the backend error envelope (top-level or FastAPI `detail`). */
interface ErrorEnvelope {
  code?: string;
  message?: string;
  detail?: { code?: string; message?: string };
}

async function parse(resp: Response): Promise<unknown> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    // Domain errors: top-level {code,message}. Guard errors (401/403/415):
    // FastAPI default {detail:{code,message}}.
    const env = (body ?? {}) as ErrorEnvelope;
    const code = env.code ?? env.detail?.code ?? "UNKNOWN";
    const message = env.message ?? env.detail?.message ?? resp.statusText;
    throw new TerritoryIdentityError(resp.status, code, message);
  }
  return body;
}

export async function resolveTerritory(
  seedParcelId: number,
  signal?: AbortSignal,
): Promise<ResolveResponse> {
  const resp = await fetch(
    `${API_BASE}/api/v1/territories/resolve?seed_parcel_id=${encodeURIComponent(seedParcelId)}`,
    { credentials: "include", headers: { "Content-Type": "application/json" }, signal },
  );
  return (await parse(resp)) as ResolveResponse;
}

export interface SaveIdentityInput {
  seedParcelId: number;
  displayName: string;
  expectedMembershipFingerprint: string;
  expectedRevision: number | null;
}

/**
 * POST a session-bound owner mutation. Attaches the memory-only CSRF token and,
 * on a CSRF_REQUIRED/CSRF_INVALID 403 (rotated/stale session token), refetches
 * the token ONCE and retries — never optimistic.
 */
async function csrfPost(path: string, body: string, signal?: AbortSignal): Promise<Response> {
  const doPost = async (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
      signal,
      body,
    });
  let resp = await doPost(await getCsrfToken());
  if (resp.status === 403) {
    const peek = await resp.clone().json().catch(() => null);
    const code = peek?.code ?? peek?.detail?.code;
    if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doPost(await getCsrfToken(true));
    }
  }
  return resp;
}

export async function saveTerritoryIdentity(
  input: SaveIdentityInput,
  signal?: AbortSignal,
): Promise<SaveResponse> {
  const body = JSON.stringify({
    seed_parcel_id: input.seedParcelId,
    display_name: input.displayName,
    expected_membership_fingerprint: input.expectedMembershipFingerprint,
    expected_revision: input.expectedRevision,
  });
  const resp = await csrfPost("/api/v1/territories/identity", body, signal);
  return (await parse(resp)) as SaveResponse;
}

export interface ReconcileInput {
  seedParcelId: number;
  survivorIdentityId: string;
  expectedMembershipFingerprint: string;
  expectedCandidates: { identityId: string; expectedRevision: number }[];
}

export async function reconcileTerritoryIdentity(
  input: ReconcileInput,
  signal?: AbortSignal,
): Promise<ReconcileResponse> {
  const body = JSON.stringify({
    seed_parcel_id: input.seedParcelId,
    survivor_identity_id: input.survivorIdentityId,
    expected_membership_fingerprint: input.expectedMembershipFingerprint,
    expected_candidates: input.expectedCandidates.map((c) => ({
      identity_id: c.identityId,
      expected_revision: c.expectedRevision,
    })),
  });
  const resp = await csrfPost("/api/v1/territories/reconcile", body, signal);
  return (await parse(resp)) as ReconcileResponse;
}
